import React, { useEffect, useMemo, useState } from "react";
import { REACT_APP_API } from "../../constants";
import {
  Button,
  Form,
  Input,
  Row,
  Select,
  Space,
  Steps,
  Tooltip,
  Typography,
  message,
  Divider,
  Card,
} from "antd";
import {
  InfoCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  DatabaseOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DbEnvError, DbEnvSecuritySteps, prepareDbEnv } from "./dbEnvInstall";
import { defaultInstallName } from "./dbAgentModel";

const { Title, Text } = Typography;
const { Step } = Steps;

const OnboardLinuxDatabase = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [notification, contextHolder] = message.useMessage();

  const [dbType, setDbType] = useState("mysql");
  const [agentKey, setAgentKey] = useState("");
  const [installName, setInstallName] = useState("");
  const [envLoading, setEnvLoading] = useState(false);
  const [envError, setEnvError] = useState("");

  const commands = [
    "sudo ./run_agent.sh add",
    "sudo ./run_agent.sh modify",
    "sudo ./run_agent.sh delete",
  ];

  useEffect(() => {
    generateAgentKey();
    form.setFieldsValue({ dbType: "mysql" });
  }, [form]);

  const generateAgentKey = () => {
    const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const sections = [8, 4, 4, 4, 12];
    sections.forEach((length) => {
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      result += "-";
    });
    result = result.slice(0, -1);
    setAgentKey(result);
  };

  const getLinuxScriptUrlAndPort = (type) => {
    if (type === "mysql") {
      return {
        scriptUrl:
          "https://raw.githubusercontent.com/authnull0/windows-endpoint/mysql-db-agent/agent/linux-build/run_agent.sh",
        dbPort: 3306,
      };
    }
    if (type === "postgres") {
      return {
        scriptUrl:
          "https://raw.githubusercontent.com/authnull0/windows-endpoint/on-prem/agent/linux-build/run_agent.sh",
        dbPort: 5432,
      };
    }
    return { scriptUrl: "<script_url>", dbPort: "" };
  };

  // The installer's own lines; the install credential and ProxySQL admin
  // login are appended by prepareDbEnv.
  const buildEnvBase = () => {
    const orgId = localStorage.getItem("OrgId") || "105";
    const tenantId = localStorage.getItem("DomainId") || "1";
    const { dbPort } = getLinuxScriptUrlAndPort(dbType);
    return [
      `ORG_ID=${orgId}`,
      `TENANT_ID=${tenantId}`,
      `DB_TYPE=${dbType}`,
      `DB_PORT=${dbPort}`,
      "TIME_INTERVAL=1",
      `API=${REACT_APP_API}`,
      `KEY=${agentKey}`,
      `MACHINE_KEY=DATAB${Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0")}`,
    ];
  };

  const downloadEnvFile = async () => {
    if (!dbType) {
      notification.error({ content: "Please select a Database Type." });
      return;
    }
    setEnvError("");
    setEnvLoading(true);
    const label = installName.trim() || defaultInstallName("Linux", dbType);
    const result = await prepareDbEnv({ label, base: buildEnvBase() });
    setEnvLoading(false);
    if (!result.ok) {
      setEnvError(result.error);
      return;
    }
    const blob = new Blob([result.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "db.env";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notification.success({ content: "db.env downloaded successfully." });
  };

  const steps = useMemo(() => {
    const options = [
      {
        title: "Database Type",
        icon: <DatabaseOutlined />,
        content: (
          <Card size="small" className="mt-4">
            <Form form={form} layout="vertical">
              <Form.Item
                label={
                  <Space>
                    <span>Database Type</span>
                    <Tooltip title="Currently, only MySQL on Linux Debian-based systems is supported.">
                      <QuestionCircleOutlined className="text-gray-400" />
                    </Tooltip>
                  </Space>
                }
                name="dbType"
                rules={[
                  { required: true, message: "Database type is required." },
                ]}
                initialValue="mysql"
              >
                <Select
                  onChange={(value) => setDbType(value)}
                  placeholder="Select Database Type"
                >
                  <Select.Option value="mysql">MySQL</Select.Option>
                  <Select.Option value="postgres">PostgreSQL</Select.Option>
                </Select>
              </Form.Item>
              <Text type="secondary" className="text-sm">
                <InfoCircleOutlined className="mr-1" /> This setup will install
                both the Database Agent and ProxySQL for enhanced security and
                monitoring on compatible Linux systems.
              </Text>
            </Form>
          </Card>
        ),
      },
      {
        title: "Download Installation Script",
        icon: <DownloadOutlined />,
        content: (
          <Card size="small" className="mt-4">
            <Text className="block mb-2">
              Run the following command to download the installation script:
            </Text>
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value={`sudo curl -L -o run_agent.sh ${getLinuxScriptUrlAndPort(dbType).scriptUrl}`}
              suffix={
                <Text
                  copyable={{
                    text: `sudo curl -L -o run_agent.sh ${
                      getLinuxScriptUrlAndPort(dbType).scriptUrl
                    }`,
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> This script will be used
              to install and configure the agent on your Linux machine.
            </Text>
          </Card>
        ),
      },
      {
        title: "Make Script Executable",
        icon: <FileTextOutlined />,
        content: (
          <Card size="small" className="mt-4">
            <Text className="block mb-2">Make the script executable:</Text>
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value="sudo chmod +x run_agent.sh"
              suffix={
                <Text
                  copyable={{
                    text: "sudo chmod +x run_agent.sh",
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> This command gives the
              script permission to run on your system.
            </Text>
          </Card>
        ),
      },
      {
        title: "Download Configuration File",
        icon: <DownloadOutlined />,
        content: (
          <Card size="small" className="mt-4">
            <Text className="block mb-4">
              Download the agent configuration file.
            </Text>
            <Space className="" wrap>
              <Input
                aria-label="Install name"
                placeholder={`Install name (e.g. ${defaultInstallName("Linux", dbType)})`}
                value={installName}
                maxLength={120}
                onChange={(e) => setInstallName(e.target.value)}
                style={{ width: "20rem" }}
              />
              <Button
                type="primary"
                onClick={downloadEnvFile}
                loading={envLoading}
                icon={<DownloadOutlined />}
              >
                Download db.env
              </Button>
            </Space>
            <DbEnvError message={envError} />
            <DbEnvSecuritySteps />
            <Divider className="my-4" />
            <Text type="secondary" className="text-sm">
              <InfoCircleOutlined className="mr-1" /> This file contains your
              unique configuration details needed for the agent to connect to
              AuthNull. The installation script will look for this file.
            </Text>
          </Card>
        ),
      },
      {
        title: "Run the Installation & Management Script",
        icon: <FileTextOutlined />,
        content: (
          <Card size="small" className="mt-4">
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value="sudo ./run_agent.sh install"
              suffix={
                <Text
                  copyable={{
                    text: "sudo ./run_agent.sh install",
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> The run_agent.sh script
              supports the following options:
              <div className="ml-5">
              <ul className="list-disc ml-5 mt-2 mb-1 space-y-2">
                <li>
                  <strong>Install</strong>
                  <br />
                  Installs the Database Agent and ProxySQL, and sets up
                  monitoring and logging..
                </li>
                <li>
                  <strong>Add</strong>
                  <br />
                  Adds new database connection details to the agent
                  configuration.
                </li>
                <li>
                  <strong>Modify</strong>
                  <br />
                  Updates existing database connection details.
                </li>
                <li>
                  <strong>Delete</strong>
                  <br />
                  Removes database connection details from the agent
                  configuration.
                </li>
                <li>
                  <strong>Examples</strong>{" "}
                  {commands.map((command) => (
                    <Input
                      key={command}
                      className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
                      readOnly
                      value={command}
                      suffix={
                        <Text
                          copyable={{
                            text: command,
                            tooltips: ["Copy Command", "Copied"],
                          }}
                          className="text-gray-500 hover:text-blue-500 cursor-pointer"
                        />
                      }
                    />
                  ))}
                </li>
              </ul></div>
              <Text type="warning" className="block mt-2 text-sm">
                The script will require the `db.env` file. The ProxySQL setup
                part might take up to 15 minutes to complete.
              </Text>
            </Text>
          </Card>
        ),
      },
      {
        title: "Verify Installation",
        icon: <CheckCircleOutlined className="text-green-500" />,
        content: (
          <Card size="small" className="mt-4">
            <Text className="block mb-2">View Database Agent logs:</Text>
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value="tail -f /var/log/authnull-db-agent.log"
              suffix={
                <Text
                  copyable={{
                    text: "tail -f /var/log/authnull-db-agent.log",
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> Check the agent log to
              ensure the service is running correctly. You should see successful
              startup and connection messages.
            </Text>
          </Card>
        ),
      },
      {
        title: "Restart the Agent",
        icon: <RetweetOutlined className="text-red-500" />,
        content: (
          <Card size="small" className="mt-4">
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value="sudo systemctl restart db-agent"
              suffix={
                <Text
                  copyable={{
                    text: "sudo systemctl restart db-agent",
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> Restart the agent service
              to reinitialize the agent with the latest settings.
            </Text>
          </Card>
        ),
      },
      {
        title: "Check Agent Status",
        icon: <InfoCircleOutlined className="text-blue-500" />,
        content: (
          <Card size="small" className="mt-4">
            <Input
              className="font-mono text-sm p-2 bg-gray-100 rounded border-none mt-2"
              readOnly
              value="sudo systemctl status db-agent"
              suffix={
                <Text
                  copyable={{
                    text: "sudo systemctl status db-agent",
                    tooltips: ["Copy Command", "Copied"],
                  }}
                  className="text-gray-500 hover:text-blue-500 cursor-pointer"
                />
              }
            />
            <Text type="secondary" className="block text-sm mt-2">
              <InfoCircleOutlined className="mr-1" /> View the agent service
              status to confirm that the service is active and running without
              errors.
            </Text>
          </Card>
        ),
      },
    ];
    return options;
  }, [dbType, agentKey, installName, envLoading, envError]);
  
  return (
    <>
      {contextHolder}
      <div className="w-full max-w-4xl p-4 md:p-6">
        <Title level={2} className="text-2xl font-semibold mb-2">
          Install Database Agent
        </Title>
        <Text type="secondary" className="block text-gray-600 mb-6 text-sm">
          Follow these steps to install and configure the AuthNull Database Agent and ProxySQL.
          <br />
          <span className="font-medium">Compatibility:</span> This agent is designed for Linux
          Debian-based systems (e.g., Ubuntu, Debian).
        </Text>
        <Steps direction="vertical" size="small" current={-1} className="mt-5 space-y-4">
          {steps.map((item, index) => (
            <Step
              key={index}
              title={
                <Text strong className="font-medium">
                  {item.title}
                </Text>
              }
              icon={item.icon}
              description={<div className="mt-2 pl-2">{item.content}</div>}
              className="pb-4"
            />
          ))}
        </Steps>
        <Row justify="end" className="mt-6">
          <Space size="middle">
            <Button type="primary" onClick={() => navigate("/pam/databases")} className="px-4 py-2">
              Finish
            </Button>
          </Space>
        </Row>
      </div>
    </>
  );
};

export default OnboardLinuxDatabase;
